// WhatsApp / Instagram chat view: same layout as the reference product's channel chat modal (title, brand
// divider, status row, Stop/Start AI replies, date-grouped bubbles). Demo additions: WhatsApp-style
// ticks on sent messages, "typing…" while the AI answers, a reply box for staff, and Clear AI memory.
//
// A human-intervention request is an alert only. The AI keeps answering until staff press Stop or
// reply here (a takeover); Start or Mark resolved hands the chat back to the AI.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaRobot, FaUser, FaUserTie, FaBell, FaInstagram, FaWhatsapp, FaFileAlt, FaMapMarkerAlt } from 'react-icons/fa';
import api, { formatPhone, SALON_NAME } from '../services/api';
import {
  Confirm,
  InterestTag,
  InterventionBadge,
  LeadFlag,
  Loader,
  Spinner,
  UpcomingTag,
  INSTAGRAM_COLOR,
  instagramHandle,
} from './ui';

const POLL_MS = 4000;
const TYPING_WINDOW_MS = 90 * 1000;

const SENDER_LABEL = { ai: `${SALON_NAME} AI`, staff: 'Staff', auto: 'Automated message' };

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dayLabel(raw) {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw || '');
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function timeLabel(raw) {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** Date dividers, plus a marker where AI memory was cleared. */
function buildItems(messages, memoryResetAt) {
  const out = [];
  let lastDay = '';
  let markerPlaced = !memoryResetAt;
  messages.forEach((m) => {
    if (!markerPlaced && m.created_at >= memoryResetAt) {
      out.push({ type: 'memory', key: 'memory' });
      markerPlaced = true;
    }
    const day = String(m.created_at || '').slice(0, 10);
    if (day !== lastDay) {
      out.push({ type: 'date', key: `d-${day}`, label: dayLabel(m.created_at) });
      lastDay = day;
    }
    out.push({ type: 'msg', key: `m-${m.id}`, msg: m });
  });
  if (!markerPlaced) out.push({ type: 'memory', key: 'memory' });
  return out;
}

/** WhatsApp-style delivery ticks for messages Nyafaz sent. */
export function Ticks({ status }) {
  if (status === 'failed') return <span className="nye-ticks nye-ticks--failed" title="Not delivered">⚠</span>;
  if (status === 'read') return <span className="nye-ticks nye-ticks--read" title="Read">✓✓</span>;
  if (status === 'delivered') return <span className="nye-ticks" title="Delivered">✓✓</span>;
  if (status === 'sent') return <span className="nye-ticks" title="Sent">✓</span>;
  return null;
}

function OutAvatar({ sender }) {
  if (sender === 'staff') {
    return (
      <div className="nye-chat-avatar nye-chat-avatar--staff" title="Staff">
        <FaUserTie size={14} />
      </div>
    );
  }
  if (sender === 'auto') {
    return (
      <div className="nye-chat-avatar nye-chat-avatar--auto" title="Automated message">
        <FaBell size={13} />
      </div>
    );
  }
  return (
    <div className="nye-chat-avatar nye-chat-avatar--bot" title={`${SALON_NAME} AI`}>
      <FaRobot size={15} />
    </div>
  );
}

const MEDIA_ICON = { image: '📷', voice: '🎤', audio: '🎵', video: '🎬', sticker: '🌟', document: '📄', location: '📍', contacts: '👤', reaction: '💬', unsupported: '⚠️' };

/** The "[Voice message]" tag the backend puts in front of a media message's caption. */
function splitMediaBody(body) {
  const match = /^\[([^\]]+)\]\s*([\s\S]*)$/.exec(String(body || ''));
  return match ? { tag: match[1], caption: match[2] } : { tag: '', caption: String(body || '') };
}

/** Photo, voice note, video, sticker or document a customer sent, loaded from object storage. */
function MediaContent({ msg }) {
  const { tag, caption } = splitMediaBody(msg.body);
  const type = msg.media_type;
  const url = msg.media_url;
  const mime = String(msg.media_mime || '').split(';')[0];
  let media = null;
  if (url && (type === 'image' || type === 'sticker')) {
    media = (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={tag} className={type === 'sticker' ? 'nye-msg-sticker' : 'nye-msg-image'} loading="lazy" />
      </a>
    );
  } else if (url && (type === 'voice' || type === 'audio')) {
    media = <audio className="nye-msg-audio" controls preload="metadata" src={url} />;
  } else if (url && type === 'video') {
    media = <video className="nye-msg-video" controls preload="metadata" src={url} />;
  } else if (url) {
    media = (
      <a href={url} target="_blank" rel="noreferrer" className="nye-msg-file">
        <FaFileAlt size={15} /> {caption || tag} {mime ? <span className="nye-muted">({mime})</span> : null}
      </a>
    );
  }
  const showCaption = caption && !(url && type === 'document');
  return (
    <>
      <div className="nye-msg-media-tag">
        {type === 'location' ? <FaMapMarkerAlt size={11} /> : MEDIA_ICON[type] || '📎'} {tag}
        {!url && ['image', 'voice', 'audio', 'video', 'sticker', 'document'].includes(type) ? (
          <span className="nye-muted"> · file not saved</span>
        ) : null}
      </div>
      {media}
      {showCaption ? <div className="nye-msg-caption">{caption}</div> : null}
    </>
  );
}

export default function ChatModal({ customerId, channel = 'whatsapp', flag, onClose, onChanged }) {
  const isInstagram = channel === 'instagram';
  const channelLabel = isInstagram ? 'Instagram' : 'WhatsApp';
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [, setTick] = useState(0);
  const bodyRef = useRef(null);
  const lastCount = useRef(0);

  const load = useCallback(
    (silent) =>
      api
        .listMessages(customerId, channel)
        .then(setThread)
        .catch((e) => !silent && toast.error(e.message))
        .finally(() => setLoading(false)),
    [customerId, channel],
  );

  useEffect(() => {
    setLoading(true);
    lastCount.current = 0;
    load(false);
    const t = setInterval(() => {
      load(true);
      setTick((n) => n + 1); // re-evaluate the typing indicator window
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const messages = useMemo(() => thread?.messages || [], [thread?.messages]);
  const customer = thread?.customer;
  const handle = instagramHandle(customer?.instagram_username);
  const name = isInstagram ? handle || customer?.name || 'Customer' : customer?.name || formatPhone(customer?.phone) || 'Customer';
  const agentActive = thread ? !thread.agent_paused : true;
  const last = messages[messages.length - 1];
  const aiTyping = Boolean(
    thread && agentActive && last && last.direction === 'in' && Date.now() - new Date(last.created_at).getTime() < TYPING_WINDOW_MS,
  );
  const items = useMemo(() => buildItems(messages, thread?.memory_reset_at), [messages, thread?.memory_reset_at]);

  useEffect(() => {
    const count = messages.length + (aiTyping ? 1 : 0);
    if (count !== lastCount.current && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
    lastCount.current = count;
  }, [messages.length, aiTyping]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !confirmReset && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, confirmReset]);

  const after = async (message) => {
    if (message) toast.success(message);
    await load(true);
    if (onChanged) onChanged();
  };

  const toggleAgent = async () => {
    setToggling(true);
    try {
      if (agentActive) {
        await api.pauseAgent(customerId);
        await after('You have taken over this chat. The AI stays quiet until you press Start.');
      } else {
        await api.resumeAgent(customerId);
        await after('The AI is answering this chat again.');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setToggling(false);
    }
  };

  const resolve = async () => {
    if (!thread?.handoff) return;
    try {
      await api.resolveHandoff(thread.handoff.id);
      await after('Marked as resolved. The AI continues this chat.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const resetMemory = async () => {
    setConfirmReset(false);
    try {
      await api.resetMemory(customerId);
      await after('AI memory cleared. The next message starts a fresh conversation.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      const res = await api.sendReply(customerId, text, channel);
      setDraft('');
      if (res && res.sent === false) toast.error(`Saved, but ${channelLabel} did not send: ${res.error}`);
      else toast.success('Reply sent. You have taken over; press Start to hand back to the AI.');
      await load(true);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="nye-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`nye-chat nye-fade${isInstagram ? ' nye-chat--instagram' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="nye-row" style={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="nye-chat-title">
              {channelLabel} : {name}
            </div>
            {customer && (customer.phone || isInstagram) ? (
              <div className="nye-small nye-muted" style={{ marginTop: 5 }}>
                {isInstagram ? (
                  <>
                    {handle ? `Instagram ID: ${handle.slice(1)}` : 'Instagram DM'}
                    {customer.phone ? ` · Mobile: ${formatPhone(customer.phone)}` : ' · Mobile not shared yet'}
                  </>
                ) : (
                  <>Phone: {formatPhone(customer.phone)}</>
                )}
                {aiTyping ? <span className="nye-typing-label"> · typing…</span> : null}
              </div>
            ) : null}
          </div>
          <div className="nye-row" style={{ gap: 10, flexShrink: 0 }}>
            {thread ? (
              <button
                type="button"
                className={`nye-btn nye-btn--xs ${agentActive ? 'nye-btn--danger-solid' : 'nye-btn--success-solid'}`}
                onClick={toggleAgent}
                disabled={toggling}
                title={agentActive ? 'Take over: stop AI replies for this chat' : 'Hand back: let the AI answer again'}
              >
                {toggling ? <Spinner light /> : null}
                {agentActive ? 'Stop' : 'Start'}
              </button>
            ) : null}
            {isInstagram ? <FaInstagram size={22} color={INSTAGRAM_COLOR} /> : <FaWhatsapp size={22} color="#25D366" />}
            <button type="button" className="nye-modal-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="nye-chat-divider" />

        {thread ? (
          <div className="nye-row nye-row--wrap" style={{ marginBottom: 12 }}>
            {flag ? <LeadFlag status={flag} /> : null}
            <span className={`nye-pill nye-pill--dot ${agentActive ? 'nye-pill--ok' : 'nye-pill--warn'}`}>
              {agentActive ? 'AI replies: On' : customer?.bot_detected ? 'AI paused: bot detected' : 'AI paused: staff have taken over'}
            </span>
            {customer?.bot_detected ? (
              <>
                <span className="nye-pill nye-pill--danger">🤖 Bot detected</span>
                <span className="nye-small nye-muted" title="Press Start if this is a real person">{customer.bot_reason}</span>
              </>
            ) : null}
            {thread.handoff ? (
              <>
                <InterventionBadge />
                <span className="nye-small nye-muted">{thread.handoff.reason}</span>
                <button type="button" className="nye-btn nye-btn--outline nye-btn--xs" onClick={resolve}>
                  Mark resolved
                </button>
              </>
            ) : null}
            <span className="nye-spacer" />
            <button
              type="button"
              className="nye-btn nye-btn--ghost nye-btn--xs"
              onClick={() => setConfirmReset(true)}
              title="The AI forgets earlier messages; they stay visible here"
            >
              🧹 Clear AI memory
            </button>
          </div>
        ) : null}

        {thread?.upcoming?.length || thread?.interest_service ? (
          <div className="nye-chat-upcoming">
            {(thread.upcoming || []).map((b) => (
              <UpcomingTag key={b.id} bookings={[b]} />
            ))}
            <InterestTag service={thread.interest_service} />
          </div>
        ) : null}

        {loading && !thread ? (
          <Loader text="Loading messages…" />
        ) : (
          <div className="nye-chat-body" ref={bodyRef}>
            {items.length === 0 ? (
              <div className="nye-empty">No messages yet.</div>
            ) : (
              items.map((item) => {
                if (item.type === 'date') {
                  return (
                    <div key={item.key} className="nye-chat-date">
                      {item.label}
                    </div>
                  );
                }
                if (item.type === 'memory') {
                  return (
                    <div key={item.key} className="nye-chat-memory">
                      AI memory cleared here. Earlier messages are not used for replies.
                    </div>
                  );
                }
                const m = item.msg;
                if (m.direction === 'in') {
                  return (
                    <div key={item.key} className="nye-msg nye-msg--in">
                      <div style={{ maxWidth: '82%' }}>
                        <div className="nye-msg-inner" style={{ maxWidth: '100%' }}>
                          <div className={`nye-msg-bubble${m.media_type ? ' nye-msg-bubble--media' : ''}`}>
                            {m.media_type ? <MediaContent msg={m} /> : m.body}
                          </div>
                          <div className="nye-chat-avatar nye-chat-avatar--customer" title={name}>
                            {initials(customer?.name) || <FaUser size={13} />}
                          </div>
                        </div>
                        <div className="nye-msg-time">{timeLabel(m.created_at)}</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={item.key} className={`nye-msg nye-msg--out${m.sender === 'staff' ? ' nye-msg--staff' : ''}${m.sender === 'auto' ? ' nye-msg--auto' : ''}`}>
                    <div className="nye-msg-inner">
                      <OutAvatar sender={m.sender} />
                      <div className="nye-msg-bubble">
                        {m.body}
                        <div className="nye-msg-meta">
                          {SENDER_LABEL[m.sender] || SALON_NAME} · {timeLabel(m.created_at)} <Ticks status={m.status} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {aiTyping ? (
              <div className="nye-msg nye-msg--out">
                <div className="nye-msg-inner">
                  <OutAvatar sender="ai" />
                  <div className="nye-msg-bubble nye-typing" aria-label={`${SALON_NAME} AI is typing`}>
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <form className="nye-chat-foot" onSubmit={send}>
          <input
            className="nye-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={agentActive ? `Reply as ${SALON_NAME} staff (this takes over from the AI)…` : `Reply as ${SALON_NAME} staff…`}
          />
          <button type="submit" className="nye-btn nye-btn--primary" disabled={sending || !draft.trim()}>
            {sending ? <Spinner light /> : null}
            Send
          </button>
        </form>
      </div>

      {confirmReset ? (
        <Confirm
          title="Clear AI memory?"
          message="The AI forgets this chat's earlier messages and any booking it was about to confirm. The messages stay visible here. Use this to start a fresh test conversation."
          confirmLabel="Clear memory"
          danger={false}
          onConfirm={resetMemory}
          onCancel={() => setConfirmReset(false)}
        />
      ) : null}
    </div>
  );
}
